import React, { useState } from 'react';
import { X, Smartphone, Monitor, Download } from 'lucide-react';

interface PWAInstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PWAInstructionsModal: React.FC<PWAInstructionsModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isDesktop = !isIOS && !isAndroid;

  const getInstructions = () => {
    if (isIOS) {
      return {
        title: "Install on iPhone/iPad",
        steps: [
          "Tap the Share button at the bottom of Safari",
          "Scroll down and tap 'Add to Home Screen'",
          "Tap 'Add' to confirm",
          "The app will appear on your home screen"
        ],
        icon: Smartphone
      };
    } else if (isAndroid) {
      return {
        title: "Install on Android",
        steps: [
          "Open this site in Chrome browser",
          "Tap the menu (three dots) in the top right",
          "Select 'Add to Home screen' or 'Install app'",
          "Tap 'Install' to confirm",
          "The app will appear on your home screen"
        ],
        icon: Smartphone
      };
    } else {
      return {
        title: "Install on Desktop",
        steps: [
          "Look for the install button in your browser's address bar",
          "Chrome/Edge: Click the install icon (⊕) in the address bar",
          "Firefox: Click the install button in the address bar",
          "Follow the browser's installation prompts",
          "The app will be added to your applications"
        ],
        icon: Monitor
      };
    }
  };

  const instructions = getInstructions();
  const IconComponent = instructions.icon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <IconComponent className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {instructions.title}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Follow these steps to install KKoS Tours as a Progressive Web App:
            </p>
            
            <ol className="space-y-3">
              {instructions.steps.map((step, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                    {step}
                  </span>
                </li>
              ))}
            </ol>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Download className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                    Benefits of Installing
                  </h3>
                  <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                    <li>• Faster loading and offline access</li>
                    <li>• App-like experience without app store</li>
                    <li>• Home screen shortcut</li>
                    <li>• Automatic updates</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
            >
              Got it!
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstructionsModal;
